import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from './SEO';
import './RoadmapPage.css';
import logo from '../assets/pokeshani-logo.png';

const RoadmapPage: React.FC = () => {
  const location = useLocation();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  const changelog = [
    {
      date: '2025-12-21',
      title: 'Advanced Data Insights',
      description: 'Launched deep analytics for creators. You can now track exact view counts, monitor conversion rates, and compare performance data across different time periods to optimize your sales.'
    },
    {
      date: '2025-12-13',
      title: 'Advanced View Tracking',
      description: 'Launched real-time product view tracking and analytics. Creators can now see exactly how many people are visiting their sales pages.'
    },
    {
      date: '2025-10-18',
      title: 'The Membership Revolution',
      description: 'Major update: Support for subscription products and memberships. Creators can now build recurring revenue streams directly on PokeShani.'
    },
    {
      date: '2025-06-15',
      title: 'PokeShani Marketplace',
      description: 'Launched the integrated marketplace inside PokeShani, making it easier for creators to get discovered and for buyers to find quality digital products.'
    },
    {
      date: '2025-05-10',
      title: 'Enhanced Purchase Receipts',
      description: 'Improved automated email delivery with beautiful receipts and clear access instructions for buyers.'
    },
    {
      date: '2025-04-15',
      title: 'Real-time Creator Analytics',
      description: 'Launched the first version of the Creator Dashboard with instant revenue and sales synchronization.'
    },
    {
      date: '2025-04-01',
      title: 'The Official Launch',
      description: 'PokeShani officially goes live! The foundation of secure, direct PayPal payouts with no platform holding period is born.'
    }
  ];

  const roadmap = [
    {
      title: 'Pro Plans & Lifetime Deals',
      description: 'We are introducing premium tiers for power users. This will include Lifetime Deals (LTD) for early adopters to get premium features forever with a one-time payment.',
      tag: 'Upcoming'
    },
    {
      title: 'Custom Domain Support',
      description: 'Connect your own domain to your PokeShani store for a fully branded experience.',
      tag: 'Upcoming'
    },
    {
      title: 'Advanced Affiliate System',
      description: 'Expanding the marketer system into a full-blown affiliate platform where you can set custom commission rates for each product.',
      tag: 'Upcoming'
    },
    {
      title: 'AI Product Description Generator',
      description: 'Use AI to write high-converting sales copy for your digital products in seconds.',
      tag: 'Upcoming'
    },
    {
      title: 'Abandoned Cart Recovery',
      description: 'Automatically follow up with potential buyers who didn\'t complete their purchase to boost your conversion rates.',
      tag: 'Upcoming'
    },
    {
      title: 'Creator Design Templates',
      description: 'Pre-designed high-converting templates for your sales pages. Just pick a style and go.',
      tag: 'Upcoming'
    }
  ];

  return (
    <>
      <SEO 
        title="Updates & Roadmap - PokeShani"
        description="Follow the journey of PokeShani. See what we've built, what we're building, and how we're helping creators keep 100% of their revenue."
        url="https://pokeshani.com/roadmap"
      />
      
      <div className="roadmap-container">
        <header className="roadmap-header">
          <Link to="/" className="roadmap-logo-link">
            <img src={logo} alt="PokeShani Logo" className="roadmap-logo" />
            <span className="roadmap-brand">PokeShani</span>
          </Link>
        </header>

        <main className="roadmap-content">
          <section className="intro-section">
            <h1 className="roadmap-main-title">Platform Updates & Roadmap</h1>
            <p className="roadmap-main-subtitle">
              PokeShani was built on a simple promise: Creators should keep what they earn. 
              No fees, no middlemen, just direct growth.
            </p>
          </section>

          <section className="changelog-section">
            <h2 className="section-title">Changelog</h2>
            <p className="section-subtitle">A history of shipping fast and listening to creators.</p>
            
            <div className="timeline">
              {changelog.map((item, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-marker">
                    <span className="timeline-date">{item.date}</span>
                    <span className="timeline-dot"></span>
                  </div>
                  <div className="timeline-content">
                    <h3 className="timeline-title">{item.title}</h3>
                    <p className="timeline-description">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="roadmap-section">
            <h2 className="section-title">Roadmap</h2>
            <p className="section-subtitle">Where we're headed next. Realistic plans, no fluff.</p>
            
            <div className="roadmap-grid">
              {roadmap.map((item, index) => (
                <div key={index} className="roadmap-card">
                  <div className="roadmap-card-header">
                    <h3 className="roadmap-card-title">{item.title}</h3>
                    <span className={`roadmap-tag ${item.tag.toLowerCase().replace(' ', '-')}`}>{item.tag}</span>
                  </div>
                  <p className="roadmap-card-description">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <footer className="roadmap-footer-note">
            <div className="feedback-box">
              <h3>We build for you.</h3>
              <p>
                PokeShani is actively improving based on user feedback. We regularly ship updates and listen closely to creator suggestions. 
                Have an idea? We're listening.
              </p>
              <a href="https://x.com/PokeShani" target="_blank" rel="noopener noreferrer" className="feedback-link">
                Tell us what to build next →
              </a>
            </div>
          </footer>
        </main>
      </div>
    </>
  );
};

export default RoadmapPage;
